import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  GLOBAL_SEARCH_DEBOUNCE_MS,
  GLOBAL_SEARCH_MIN_LENGTH,
  type GlobalSearchHit,
  type GlobalSearchResults,
} from "api/globalSearchApi";
import { orderDetailRoute, stagedOrderDetailRoute } from "constants/routes";
import { useGlobalSearchDropdownQuery } from "features/globalSearch/api/useGlobalSearchDropdownQuery";
import { searchResultsRoute } from "features/globalSearch/searchResultsRoute";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { useOutsidePress } from "hooks/useOutsidePress";
import IconSalesOrders from "components/icons/IconSalesOrders";
import IconStagedOrders from "components/icons/IconStagedOrders";
import { StatusTag } from "components/ui/StatusTag/StatusTag";
import {
  PAYMENT_TAG_COLOR,
  STATUS_TAG_COLOR,
} from "features/globalSearch/constants";
import { formatDate as formatOrderDate } from "features/orders/utils/mapOrders";

interface SearchBlockProps {
  className?: string;
  onSearch?: (value: string) => void;
  /** Focus the input on mount — the mobile header mounts this block
   *  only when its search icon expands the field, and the keyboard
   *  should be up immediately. */
  autoFocus?: boolean;
}

const SEARCH_SHORTCUT_KEY = "k";

// ── Tailwind class bundles ────────────────────────────────────────────────
// The container picks up `:has()` rules from the original CSS — when the
// child input is `:disabled`, the container desaturates and the inner icon /
// shortcut / input pickup the muted token. Tailwind exposes this via the
// `has-[input:disabled]:` and `[&_.foo]:` arbitrary variants.
/** What a failed read shows: the empty state, counts included. */
const EMPTY_RESULTS: GlobalSearchResults = {
  orders: [],
  stagedOrders: [],
  counts: { orders: 0, stagedOrders: 0 },
};

// `relative` anchors the results dropdown.
const CONTAINER_CLASS =
  'relative inline-flex items-center gap-2 h-9 px-3' +
  ' bg-[var(--color-bg-secondary)] border border-solid border-[var(--color-border)]' +
  ' text-[var(--color-text-secondary)] w-full max-w-[520px] min-w-[320px]' +
  ' max-[768px]:min-w-0' +
  ' transition-[border-color,box-shadow,background-color] duration-[150ms] ease-linear' +
  ' focus-within:border-[var(--color-accent)]' +
  ' focus-within:shadow-[0_0_0_2px_rgba(37,99,235,0.15)]' +
  ' focus-within:bg-[var(--color-bg-elevated)]' +
  // Disabled state — recolour every child, drop the focus glow, dim opacity.
  ' has-[input:disabled]:bg-[var(--color-bg-secondary)]' +
  ' has-[input:disabled]:border-[var(--color-border)]' +
  ' has-[input:disabled]:shadow-none' +
  ' has-[input:disabled]:opacity-60' +
  ' has-[input:disabled]:cursor-not-allowed';

const ICON_CLASS = 'search-block__icon inline-flex shrink-0 text-[var(--color-text-muted)]';

// Input — flexes to fill the row. WebKit/IE clear buttons are aggressively
// hidden so our custom × never duplicates with the browser's native one
// (belt-and-suspenders even with type="text").
const INPUT_CLASS =
  // `outline-none` (not `outline-0`): Chrome's UA focus ring is
  // `outline: auto`, which ignores a zero width — only a transparent
  // outline actually suppresses the blue ring. The container's
  // `focus-within` border/glow provides the visible focus state.
  'search-block__input flex-1 border-0 outline-none focus:outline-none bg-transparent' +
  ' text-[var(--color-text-primary)] text-sm min-w-0' +
  ' placeholder:text-[var(--color-text-muted)]' +
  ' disabled:cursor-not-allowed' +
  ' [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-cancel-button]:hidden' +
  ' [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-decoration]:hidden' +
  ' [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-button]:hidden' +
  ' [&::-webkit-search-results-decoration]:appearance-none [&::-webkit-search-results-decoration]:hidden' +
  ' [&::-ms-clear]:hidden [&::-ms-reveal]:hidden';

// The CTRL+K hint is a physical-keyboard affordance — hidden on mobile.
const SHORTCUT_CLASS =
  'search-block__shortcut text-[11px] font-hk-medium text-[var(--color-text-muted)]' +
  ' border border-solid border-[var(--color-border)] rounded px-[6px] py-[2px] whitespace-nowrap' +
  ' max-[768px]:hidden';

const CLEAR_CLASS =
  'inline-flex items-center justify-center w-[22px] h-[22px] border-0 bg-transparent' +
  ' text-[var(--color-text-muted)] text-base leading-none cursor-pointer p-0 rounded-full' +
  ' transition-[background-color,color] duration-[120ms] ease-linear' +
  ' hover:text-[var(--color-text-primary)] hover:bg-[rgba(15,23,42,0.08)]' +
  ' focus-visible:text-[var(--color-text-primary)] focus-visible:bg-[rgba(15,23,42,0.08)] focus-visible:outline-none' +
  ' dark:hover:bg-[rgba(255,255,255,0.12)] dark:focus-visible:bg-[rgba(255,255,255,0.12)]';

// ── Results dropdown (Vue `SearchBlock.vue` popover parity) ───────────────
// flex-col so the scrollable hit list and the pinned "Press Enter"
// footer are siblings — the footer never scrolls away.
const PANEL_CLASS =
  'absolute left-0 right-0 top-[calc(100%+6px)] z-[1300] max-h-[60vh] flex flex-col' +
  ' bg-[var(--color-bg-elevated)] border border-solid border-[var(--color-border)]' +
  ' rounded-lg shadow-[var(--shadow-lg)] py-1 text-left';
const PANEL_SCROLL_CLASS = 'flex-1 min-h-0 overflow-y-auto';
// Domain chips row (Vue: the popover's domain pills) — one pill per
// domain that has hits.
const CHIP_ROW_CLASS =
  'flex items-center gap-2 px-3 pt-2 pb-1.5 border-b border-solid' +
  ' border-[var(--color-divider)]';
const CHIP_CLASS =
  'inline-flex items-center px-3 py-1 rounded-full text-[12px]' +
  ' border border-solid border-[var(--color-border)]' +
  ' text-[var(--color-text-primary)]';
const GROUP_LABEL_CLASS =
  'block px-3 pt-2 pb-1 text-[10.5px] font-hk-bold tracking-[0.4px] uppercase' +
  ' text-[var(--color-text-muted)] border-b border-solid border-[var(--color-divider)]';
const ROW_CLASS =
  'w-full flex items-start gap-2.5 px-3 py-2 border-0 bg-transparent cursor-pointer text-left' +
  ' hover:bg-[var(--color-bg-hover)] transition-colors duration-fast';
const ROW_ICON_CLASS = 'flex-none mt-[2px] text-[var(--color-text-secondary)]';
const ROW_NUMBER_CLASS =
  'text-[13px] font-hk-semibold text-[var(--color-text-primary)] whitespace-nowrap';
const ROW_SUB_CLASS =
  'mt-0.5 text-[12px] text-[var(--color-text-muted)]';
const PANEL_MESSAGE_CLASS =
  'block px-3 py-3 text-[12.5px] text-[var(--color-text-muted)]';
// Pinned footer — `[Press Enter ↵] to search all \`query\``.
const FOOTER_CLASS =
  'flex items-center gap-2 px-3 py-2 border-t border-solid' +
  ' border-[var(--color-divider)] text-[12.5px] text-[var(--color-text-secondary)]';
const FOOTER_BTN_CLASS =
  'inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12.5px]' +
  ' border border-solid border-[var(--color-border)] bg-transparent cursor-pointer' +
  ' text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]' +
  ' hover:border-[var(--color-accent)] transition-colors duration-fast';


const SearchBlock: React.FC<SearchBlockProps> = ({
  className,
  onSearch,
  autoFocus = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [value, setValue] = useState<string>("");
  const [panelOpen, setPanelOpen] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Mount-time focus for the mobile expand-on-tap flow. `autoFocus`
  // never changes after mount, so the effect still fires exactly once.
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent): void => {
      if (
        event.ctrlKey &&
        event.key.toLowerCase() === SEARCH_SHORTCUT_KEY &&
        inputRef.current !== null
      ) {
        event.preventDefault();
        inputRef.current.focus();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  // Close the panel on any outside press. (Not `Popover`: that primitive
  // clones a toggle trigger, and the search box is a field the user keeps
  // typing into, not a toggle.)
  const closePanel = useCallback((): void => setPanelOpen(false), []);
  useOutsidePress(containerRef, closePanel, panelOpen);

  // Debounced server read — Vue parity: 500 ms debounce, min 3 chars
  // (`SearchBlock.vue` MIN_INPUT_LENGTH / lodash debounce 500). The
  // debounce is the shared hook and the read is a TanStack query, so a
  // retyped query is served from the cache, an in-flight one is aborted
  // through the query's own signal, and the panel's three states come from
  // the query rather than hand-rolled flags — the same model
  // `useGlobalSearchResults` uses for the results page.
  const query = value.trim();
  const debouncedQuery = useDebouncedValue(query, GLOBAL_SEARCH_DEBOUNCE_MS);
  const enabled = debouncedQuery.length >= GLOBAL_SEARCH_MIN_LENGTH;
  const dropdown = useGlobalSearchDropdownQuery(debouncedQuery);
  // The panel follows the typed value (not the debounced one), so it opens
  // the moment the query is long enough and shows "Searching…" until the
  // debounce settles and the read lands.
  useEffect(() => {
    setPanelOpen(query.length >= GLOBAL_SEARCH_MIN_LENGTH);
  }, [query]);
  const settled = debouncedQuery === query;
  const loading = !settled || (enabled && dropdown.isPending);
  // Results are shown only for a settled, long-enough query; a failed read
  // degrades to the empty state (the failure is already logged).
  const showResults = enabled && settled;
  let results: GlobalSearchResults | null = null;
  if (showResults) {
    results = dropdown.isError ? EMPTY_RESULTS : dropdown.data ?? null;
  }

  const closeAndReset = useCallback((): void => {
    setPanelOpen(false);
    setValue("");
  }, []);

  /** Vue parity (`handleSelectChange`): both kinds of hit open the
   *  order's OWN page — `/orders/sales-orders/<refId>` or
   *  `/orders/staged-orders/<refId>` — never a filtered listing, whose
   *  default date window would hide any order outside it. */
  const handleSelectOrder = useCallback(
    (hit: GlobalSearchHit): void => {
      closeAndReset();
      navigate(orderDetailRoute(hit.refId));
    },
    [closeAndReset, navigate],
  );

  const handleSelectStaged = useCallback(
    (hit: GlobalSearchHit): void => {
      closeAndReset();
      navigate(stagedOrderDetailRoute(hit.refId));
    },
    [closeAndReset, navigate],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setValue(event.target.value);
    },
    [],
  );

  // Enter / footer button — Vue parity ('show-all-enter'): route to
  // the `/search` results page with the query, defaulting the
  // Sales-orders tab.
  const submitFullSearch = useCallback((): void => {
    if (onSearch !== undefined) onSearch(query);
    if (query.length < GLOBAL_SEARCH_MIN_LENGTH) return;
    closeAndReset();
    navigate(searchResultsRoute(query));
  }, [query, onSearch, closeAndReset, navigate]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === "Escape") {
        // A keyboard user can dismiss the overlay without a pointer.
        closePanel();
        return;
      }
      if (event.key !== "Enter") return;
      submitFullSearch();
    },
    [closePanel, submitFullSearch],
  );

  const handleClear = useCallback((): void => {
    closeAndReset();
    inputRef.current?.focus();
  }, [closeAndReset]);

  const containerClass =
    className === undefined ? CONTAINER_CLASS : `${CONTAINER_CLASS} ${className}`;

  const hasHits =
    results !== null &&
    (results.orders.length > 0 || results.stagedOrders.length > 0);

  const renderGroup = (
    label: string,
    count: number,
    hits: ReadonlyArray<GlobalSearchHit>,
    onSelect: (hit: GlobalSearchHit) => void,
    icon: React.ReactNode,
  ): React.ReactNode => {
    if (hits.length === 0) return null;
    return (
      <>
        <span className={GROUP_LABEL_CLASS}>
          {label} ({count.toLocaleString()})
        </span>
        {hits.map((hit) => {
          const sub = [hit.customer, formatOrderDate(hit.date)]
            .filter((part) => part !== '' && part !== 'N/A')
            .join(' • ');
          return (
            <button
              key={hit.refId}
              type="button"
              className={ROW_CLASS}
              onClick={() => onSelect(hit)}
            >
              <span className={ROW_ICON_CLASS} aria-hidden>
                {icon}
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-2 flex-wrap">
                  <span className={ROW_NUMBER_CLASS}>{hit.number}</span>
                  {hit.status !== '' ? (
                    <StatusTag
                      label={hit.status}
                      color={
                        STATUS_TAG_COLOR[hit.status.toLowerCase()] ?? 'black'
                      }
                    />
                  ) : null}
                  {hit.paymentStatus !== '' ? (
                    <StatusTag
                      label={hit.paymentStatus}
                      color={
                        PAYMENT_TAG_COLOR[hit.paymentStatus.toLowerCase()] ??
                        'black'
                      }
                    />
                  ) : null}
                </span>
                {sub !== '' ? <span className={ROW_SUB_CLASS}>{sub}</span> : null}
              </span>
            </button>
          );
        })}
      </>
    );
  };

  return (
    <div ref={containerRef} className={containerClass}>
      <span className={ICON_CLASS} aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle
            cx="11"
            cy="11"
            r="7"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <path
            d="m20 20-3.5-3.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </span>
      <input
        ref={inputRef}
        // type="text" rather than "search" so the browser doesn't render its
        // own clear button alongside our custom one.
        type="text"
        autoComplete="off"
        className={INPUT_CLASS}
        placeholder={t("header.search.placeholder")}
        aria-label={t("header.search.placeholder")}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      {value.length > 0 ? (
        <button
          type="button"
          className={CLEAR_CLASS}
          onClick={handleClear}
          aria-label="Clear search">
          ×
        </button>
      ) : (
        <span className={SHORTCUT_CLASS} aria-hidden="true">
          {t("header.search.shortcut")}
        </span>
      )}

      {panelOpen ? (
        // `<section>` (region role) rather than role="listbox" — the rows
        // are plain buttons, not managed option children (S6819).
        <section
          className={PANEL_CLASS}
          aria-label={t("header.globalSearch.results", "Search results")}
          // The body swaps between "Searching…", the hits and "No results"
          // purely through state; announce those transitions.
          aria-live="polite"
        >
          {renderPanelContent()}
        </section>
      ) : null}
    </div>
  );

  /** Dropdown body: loading → chips + grouped hits + footer → empty. */
  function renderPanelContent(): React.ReactNode {
    if (loading) {
      return (
        <span className={PANEL_MESSAGE_CLASS}>
          {t("header.globalSearch.loading", "Searching…")}
        </span>
      );
    }
    if (results !== null && hasHits) {
      return (
        <>
          <div className={CHIP_ROW_CLASS}>
            {results.orders.length > 0 ? (
              <span className={CHIP_CLASS}>
                {t("header.globalSearch.salesOrders", "Sales orders")}
              </span>
            ) : null}
            {results.stagedOrders.length > 0 ? (
              <span className={CHIP_CLASS}>
                {t("header.globalSearch.stagedOrders", "Staged orders")}
              </span>
            ) : null}
          </div>
          <div className={PANEL_SCROLL_CLASS}>
            {renderGroup(
              t("header.globalSearch.salesOrders", "Sales orders"),
              results.counts.orders,
              results.orders,
              handleSelectOrder,
              <IconSalesOrders size={16} ariaHidden />,
            )}
            {renderGroup(
              t("header.globalSearch.stagedOrders", "Staged orders"),
              results.counts.stagedOrders,
              results.stagedOrders,
              handleSelectStaged,
              <IconStagedOrders size={16} ariaHidden />,
            )}
          </div>
          <div className={FOOTER_CLASS}>
            <button
              type="button"
              className={FOOTER_BTN_CLASS}
              onClick={submitFullSearch}
            >
              {t("header.globalSearch.pressEnter", "Press Enter")}
              <span aria-hidden>↵</span>
            </button>
            <span>
              {t("header.globalSearch.searchValue", "to search all ‘{{query}}’", {
                query: value.trim(),
              })}
            </span>
          </div>
        </>
      );
    }
    return (
      <span className={PANEL_MESSAGE_CLASS}>
        {t("header.globalSearch.noResults", "No results found")}
      </span>
    );
  }
};

export default SearchBlock;
